import { useRef, useEffect, useCallback } from 'react';

export const useSoundEngine = (isMuted) => {
  const audioCtx = useRef(null);
  const masterGain = useRef(null);
  const masterFilter = useRef(null);
  const engineOsc = useRef(null);
  const engineGain = useRef(null);

  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtx.current = ctx;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(6000, ctx.currentTime);
    masterFilter.current = filter;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(1.0, ctx.currentTime);
    masterGain.current = gain;
    filter.connect(gain);
    gain.connect(ctx.destination);

    const eGain = ctx.createGain();
    eGain.gain.setValueAtTime(0.07, ctx.currentTime);

    const subOsc = ctx.createOscillator();
    subOsc.type = "sine";
    subOsc.frequency.setValueAtTime(28, ctx.currentTime);

    const bodyOsc = ctx.createOscillator();
    bodyOsc.type = "triangle";
    bodyOsc.frequency.setValueAtTime(55, ctx.currentTime);

    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(0.6, ctx.currentTime);

    const subGain = ctx.createGain();
    const bodyGain = ctx.createGain();
    const lfoGain = ctx.createGain();

    subGain.gain.value = 0.06;
    bodyGain.gain.value = 0.04;
    lfoGain.gain.value = 4;

    lfo.connect(lfoGain);
    lfoGain.connect(bodyOsc.frequency);

    subOsc.connect(subGain);
    bodyOsc.connect(bodyGain);

    subGain.connect(eGain);
    bodyGain.connect(eGain);

    eGain.connect(filter);

    subOsc.start();
    bodyOsc.start();
    lfo.start();

    engineOsc.current = bodyOsc;
    engineGain.current = eGain;

    const resumeAudio = () => {
      if (ctx.state === 'suspended') ctx.resume();
    };
    window.addEventListener('pointerdown', resumeAudio);
    window.addEventListener('keydown', resumeAudio);

    return () => {
      window.removeEventListener('pointerdown', resumeAudio);
      window.removeEventListener('keydown', resumeAudio);
      ctx.close();
    };
  }, []);

  useEffect(() => {
    if (masterGain.current && audioCtx.current) {
      masterGain.current.gain.setTargetAtTime(isMuted ? 0 : 0.6, audioCtx.current.currentTime, 0.1);
    }
  }, [isMuted]);

  const setEngineVelocity = useCallback((velocity) => {
    if (!engineOsc.current || isMuted) return;
    const rpm = 55 + velocity / 15;

    engineOsc.current.frequency.setTargetAtTime(
      rpm,
      audioCtx.current.currentTime,
      0.3
    );

    engineGain.current.gain.setTargetAtTime(
      0.07 + velocity / 5000,
      audioCtx.current.currentTime,
      0.3
    );

  }, [isMuted]);

  const playClick = useCallback((pan = 0) => {
    if (isMuted || !audioCtx.current) return;
    const ctx = audioCtx.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const panner = ctx.createStereoPanner();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.04);
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
    panner.pan.setValueAtTime(pan, ctx.currentTime);
    osc.connect(gain);
    gain.connect(panner);
    panner.connect(masterFilter.current);
    osc.start();
    osc.stop(ctx.currentTime + 0.05);
  }, [isMuted]);

  const playSwoosh = useCallback((pan = 0) => {
    if (isMuted || !audioCtx.current) return;
    const ctx = audioCtx.current;
    const noise = ctx.createBufferSource();
    const bufferSize = ctx.sampleRate * 0.6;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(80, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.2);
    filter.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.6);
    const gain = ctx.createGain();
    const panner = ctx.createStereoPanner();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    panner.pan.setValueAtTime(pan, ctx.currentTime);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(masterFilter.current);
    noise.start();
  }, [isMuted]);

  const playIgnition = useCallback((phase) => {

    if (isMuted || !audioCtx.current) return;
    const ctx = audioCtx.current;
    const now = ctx.currentTime;
    engineGain.current.gain.setTargetAtTime(0.01, now, 0.2);

    if (phase === 1) {
      engineGain.current.gain.setTargetAtTime(0.05, now, 1.5);
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(110, now);
      osc.frequency.linearRampToValueAtTime(220, now + 1.5);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.12, now + 0.8);
      g.gain.linearRampToValueAtTime(0, now + 2.0);
      osc.connect(g);
      g.connect(masterFilter.current);
      osc.start();
      osc.stop(now + 2.0);
    }
    if (phase === 2) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(150, now);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.08, now + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
      osc.connect(g);
      g.connect(masterFilter.current);
      osc.start();
      osc.stop(now + 0.6);
    }
    if (phase === 3) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(100, now);
      osc.frequency.exponentialRampToValueAtTime(400, now + 0.8);
      g.gain.setValueAtTime(0.18, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
      osc.connect(g);
      g.connect(masterFilter.current);
      osc.start();
      osc.stop(now + 0.8);
    }
    if (phase === 4) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1500, now);
      g.gain.setValueAtTime(0.1, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.connect(g);
      g.connect(masterFilter.current);
      osc.start();
      osc.stop(now + 0.1);

    }
  }, [isMuted]);

  return { playClick, playSwoosh, playIgnition, setEngineVelocity };
};
